<template>
  <div v-if="showContentLoader" class="loader-wrapper">
    <tiny-loader class="loader-class" variant="secondary-black" />
  </div>
  <div v-else ref="contentRef" class="page">
    <riseup-header :flow-config-id="otsJobConfig.flowConfigId" :white-label-firm-id="whiteLabelFirmId" />
    <div ref="content">
      <missing-data
        v-if="shouldShowMissingData"
        @connect-accounts-click="handleConnectAccountsClick"
      />
      <six-months-summary
        :monthly-avg-income-spending="monthlyAvgIncomeSpending"
        :monthly-avg-disposable-income="monthlyAvgDisposableIncome"
        :is-data-missing="isSixMonthsDataMissing"
        @connect-accounts-click="handleConnectAccountsClick"
      />
      <spending-categories
        :monthly-category-averages="monthlyCategoryAverages"
        :is-data-missing="isSpendingCategoriesDataMissing"
        @connect-accounts-click="handleConnectAccountsClick"
      />
      <tips
        :tips="tips"
        :total-savings="totalSavings"
        :is-data-missing="isTipsDataMissing"
        @connect-accounts-click="handleConnectAccountsClick"
      />
      <div v-if="!shouldShowMissingData">
        <net-balance
          v-if="!shouldShowSavingPotential"
          :monthly-avg-disposable-income="monthlyAvgDisposableIncome"
          variant="netBalance"
        />
        <cashflow-recs
          :mortgages="syndesiCategories.mortgage_expenses_per_month"
          :insurances="syndesiCategories.insurance_and_both_expenses_per_month"
          :white-label-firm-id="whiteLabelFirmId"
          @mortgage-click="() => navigateToSyndesi('SYNDESI_MORTGAGE')"
          @insurance-click="() => navigateToSyndesi('SYNDESI_INSURANCE')"
          @pensions-click="() => navigateToSyndesi('SYNDESI_PENSION')"
        />
        <net-balance
          v-if="shouldShowSavingPotential"
          :monthly-avg-disposable-income="monthlyAvgDisposableIncome"
          variant="savingPotential"
        />
        <spare-cash
          v-if="shouldShowSavingPotential"
          :monthly-avg-disposable-income="monthlyAvgDisposableIncome"
          @button-click="() => navigateToSyndesi('SYNDESI_SAVINGS')"
        />
        <expert-advice @button-click="() => navigateToSyndesi('SYNDESI_BASE')" />
        <accountant-loop />
      </div>
    </div>
  </div>
  <error-toast
    v-if="showErrorToast"
    :text="errorMessage"
    :reason="errorReason"
    @close="showErrorToast = false"
  />
</template>

<script setup>
import TinyLoader from '@riseupil/base-ui/src/components/TinyLoader/TinyLoader.vue';
import Cookies from 'js-cookie';
import { computed, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { getTrueLayerUrl } from '../../../api/trueLayer';
import useContentLoader from '../../../composables/useContentLoader';
import config from '../../../config';
import Segment from '../../../Segment';
import { useCashflow } from '../../../services/cashflowService';
import { useInsights } from '../../../services/insightsService';
import { useOtsJob } from '../../../services/otsConfigService';
import { useSessionStore } from '../../../stores/session';
import { WhiteLabelFirmIdType } from '../../../types/otsConfig';
import ErrorToast from '../../Common/ErrorToast.vue';
import RiseupHeader from '../../Common/RiseupHeader.vue';
import MissingData from '../shared-components/MissingData.vue';
import AccountantLoop from './components/AcountantLoop.vue';
import CashflowRecs from './components/CashflowRecs.vue';
import ExpertAdvice from './components/ExpertAdvice.vue';
import NetBalance from './components/NetBalance.vue';
import SixMonthsSummary from './components/SixMonthsSummary.vue';
import SpareCash from './components/SpareCash.vue';
import SpendingCategories from './components/SpendingCategories.vue';
import Tips from './components/Tips.vue';

const { insights, populateInsights } = useInsights();
const contentRef = ref(null);
const route = useRoute();

const whiteLabelFirmId = computed(() => (
  route.path.includes('syndesi-wl')
    ? WhiteLabelFirmIdType.DefaultFirm
    : WhiteLabelFirmIdType.DNS
));

const { getOtsJobConfig } = useOtsJob();
const {
  monthlyCategoryAverages,
  monthlyAvgDisposableIncome,
  monthlyAvgIncomeSpending,
  loadCashflowData,
  isCashflowLoaded,
  loadSyndesiCategories,
  syndesiCategories,
  isSyndesiCategoriesLoaded,
} = useCashflow();

const SYNDESI_DESTINATION = {
  SYNDESI_BASE: 'syndesi_base',
  SYNDESI_INSURANCE: 'syndesi_insurance',
  SYNDESI_MORTGAGE: 'syndesi_mortgage',
  SYNDESI_PENSION: 'syndesi_pension',
  SYNDESI_SAVINGS: 'syndesi_savings',
};
const tips = ref([]);
const totalSavings = ref(0);
const otsJobConfig = await getOtsJobConfig();
const sessionStore = useSessionStore();
const showErrorToast = ref(false);
const errorMessage = ref('');
const errorReason = ref('');

const {
  showContentLoader,
  toggleLoader,
} = useContentLoader();

const ERROR_MESSAGES = {
  INSIGHTS_ONLY: 'Failed to load your saving tips. We are looking into it. Try again later.',
  CASHFLOW_ONLY: 'Oops! There was an error with your account data. We are looking into it. Try again later.',
};

const handleErrors = (insightsFailed, cashflowFailed) => {
  if (!insightsFailed && !cashflowFailed) {
    return;
  }

  if (cashflowFailed) {
    errorMessage.value = ERROR_MESSAGES.CASHFLOW_ONLY;
    errorReason.value = 'cashflow_data_load_failed';
  } else {
    errorMessage.value = ERROR_MESSAGES.INSIGHTS_ONLY;
    errorReason.value = 'insights_data_load_failed';
  }

  showErrorToast.value = true;
};

// Centralized missing data logic
const isSixMonthsDataMissing = computed(() =>
  monthlyAvgIncomeSpending.value.income === 0
  || monthlyAvgIncomeSpending.value.spending === 0
  || monthlyAvgDisposableIncome.value.total === 0);

const isSpendingCategoriesDataMissing = computed(() =>
  monthlyCategoryAverages.value.length < 3);

const isTipsDataMissing = computed(() => tips.value.length === 0);

const shouldShowMissingData = computed(() =>
  isSixMonthsDataMissing.value
  || isSpendingCategoriesDataMissing.value
  || isTipsDataMissing.value);

onMounted(async () => {
  toggleLoader(true);
  const queryParam = route.query || null;
  const shouldReload = route.state?.shouldReload;

  let insightsFailed = false;
  let cashflowFailed = false;

  if (!insights.value || insights.value.length === 0 || shouldReload) {
    try {
      await populateInsights(otsJobConfig.jobId);
    } catch (error) {
      insightsFailed = true;
    }
  }

  Segment.trackUserGot('SyndesiOtsCustomSuccessPageEntered', {
    fromEmail: queryParam ? queryParam.fromEmail : false,
    otsJobConfig,
    whiteLabelFirmId: whiteLabelFirmId.value,
  });

  if ((insights.value && insights.value.length > 0) || shouldReload) {
    tips.value = insights.value.map(insight => ({
      text: insight.item.text,
      amount: insight.item.amount,
      index: insight.index,
    }));

    totalSavings.value = insights.value.reduce((sum, insight) => sum + insight.item.amount, 0);
  }
  if (!isCashflowLoaded.value || shouldReload) {
    try {
      await loadCashflowData(6);
    } catch (error) {
      cashflowFailed = true;
    }
  }

  if (!isSyndesiCategoriesLoaded.value || shouldReload) {
    try {
      await loadSyndesiCategories();
    } catch (error) {
      cashflowFailed = true;
    }
  }

  handleErrors(insightsFailed, cashflowFailed);
  toggleLoader(false);
});

const shouldShowSavingPotential = computed(() => monthlyAvgDisposableIncome.value.total > 0);

const navigateToSyndesi = (destination = 'SYNDESI_BASE') => {
  const { customerId } = sessionStore;
  window.open(`${config.get().ruthUrl}/ots?customer_id=${customerId}&to=${SYNDESI_DESTINATION[destination]}`, '_blank');
};

const handleConnectAccountsClick = async () => {
  Segment.trackUserGot('SyndesiConnectOtsAccountsConnectClicked', {
    gclid: Cookies.get('gclid'),
    whiteLabelFirmId: whiteLabelFirmId.value,
  });
  const env = whiteLabelFirmId.value === WhiteLabelFirmIdType.DefaultFirm
    ? config.get().otsSyndesiWlSuccessEnv
    : config.get().otsSyndesiSuccessEnv;
  window.location.href = await getTrueLayerUrl({ jobId: otsJobConfig.jobId, fromSuccess: true }, env);
};
</script>

<style lang="scss" scoped>
@import '../../../scss/riseup-vars';

.content {
  margin-top: 30px;
  padding-left: 25px;
  padding-right: 25px;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.page {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: auto;
  scroll-behavior: smooth;
  font-family: CircularStdBook, serif;
}
</style>
